import { CSSProperties, KeyboardEvent, MouseEvent, ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog } from '../../../components/dialog-portal/dialog.tsx';
import { ManualTask } from '../../../types/sharedTypeImpl.ts';
import { formatDatetime } from '../../../utils/fieldFormats.ts';

// Must match .manual-task-hover-label min-height in CSS
const HOVER_HEADER_HEIGHT_PX = 35;

interface HoverPanel {
    top: number;
    rowHeight: number;
    task: ManualTask;
    rowBg: string;
}

interface DimmerCutout {
    top: number;
    left: number;
    width: number;
    height: number;
}

interface Props {
    style?: CSSProperties;
    tasks: ManualTask[];
    useLargeFont?: boolean;
    headerActions?: ReactNode;
    onEditTaskClick?: (task: ManualTask) => void;
    onCancelTaskClick?: (task: ManualTask) => void;
    onCompleteTaskClick?: (task: ManualTask) => void;
}

export function ManualTasksTableCompact({
    style,
    tasks,
    useLargeFont,
    headerActions,
    onEditTaskClick,
    onCancelTaskClick,
    onCompleteTaskClick,
}: Props) {
    const [selectedTask, setSelectedTask] = useState<ManualTask | null>(null);
    const [hoverPanel, setHoverPanel] = useState<HoverPanel | null>(null);
    const [dimmerCutout, setDimmerCutout] = useState<DimmerCutout | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    // Track which row was last entered so moving directly between rows doesn't clear the panel
    const lastEnteredKey = useRef<string | null>(null);
    const isOverExtension = useRef(false);
    const isDimmerActive = !!hoverPanel && !selectedTask;

    function getCreatedByWithEdit(task: ManualTask) {
        const lastEditedBy = task.edits?.at(-1)?.editedBy;
        const showEditor = lastEditedBy && lastEditedBy !== task.createdBy;
        return showEditor ? `${task.createdBy}, ${lastEditedBy}` : task.createdBy;
    }

    function closeSelectedTask() {
        setSelectedTask(null);
    }

    function handleActionClick(action?: (task: ManualTask) => void) {
        if (!selectedTask || !action) return;
        action(selectedTask);
        closeSelectedTask();
    }

    function handleRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, task: ManualTask) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setSelectedTask(task);
    }

    function handleRowMouseEnter(e: MouseEvent<HTMLTableRowElement>, task: ManualTask, rowKey: string, isEvenRow: boolean) {
        lastEnteredKey.current = rowKey;
        const tr = e.currentTarget;
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const wrapperRect = wrapper.getBoundingClientRect();
        const trRect = tr.getBoundingClientRect();
        const rowBg = isEvenRow ? '#ebebeb' : '#ffffff';
        setDimmerCutout({
            top: wrapperRect.top,
            left: wrapperRect.left,
            width: wrapperRect.width,
            height: wrapperRect.height,
        });
        setHoverPanel({
            top: trRect.top - wrapperRect.top,
            rowHeight: trRect.height,
            task,
            rowBg,
        });
    }

    function handleRowMouseLeave(rowKey: string) {
        // Only clear if the mouse hasn't already entered a different row
        setTimeout(() => {
            if (lastEnteredKey.current === rowKey && !isOverExtension.current) {
                setHoverPanel(null);
                setDimmerCutout(null);
            }
        }, 40);
    }

    function handleExtensionMouseEnter() {
        isOverExtension.current = true;
    }

    function handleExtensionMouseLeave() {
        isOverExtension.current = false;
        setHoverPanel(null);
        setDimmerCutout(null);
    }

    return (
        <>
            {createPortal(
                <div
                    className={`manual-task-hover-dimmer-frame ${isDimmerActive && dimmerCutout ? 'manual-task-hover-dimmer-frame--active' : ''}`.trim()}
                >
                    {dimmerCutout && (
                        <>
                            <div
                                className='manual-task-hover-dimmer-slab'
                                style={{ top: 0, left: 0, width: '100vw', height: Math.max(0, dimmerCutout.top) }}
                            />
                            <div
                                className='manual-task-hover-dimmer-slab'
                                style={{
                                    top: Math.max(0, dimmerCutout.top),
                                    left: 0,
                                    width: Math.max(0, dimmerCutout.left),
                                    height: Math.max(0, dimmerCutout.height),
                                }}
                            />
                            <div
                                className='manual-task-hover-dimmer-slab'
                                style={{
                                    top: Math.max(0, dimmerCutout.top),
                                    left: Math.max(0, dimmerCutout.left + dimmerCutout.width),
                                    width: `calc(100vw - ${Math.max(0, dimmerCutout.left + dimmerCutout.width)}px)`,
                                    height: Math.max(0, dimmerCutout.height),
                                }}
                            />
                            <div
                                className='manual-task-hover-dimmer-slab'
                                style={{
                                    top: Math.max(0, dimmerCutout.top + dimmerCutout.height),
                                    left: 0,
                                    width: '100vw',
                                    height: `calc(100vh - ${Math.max(0, dimmerCutout.top + dimmerCutout.height)}px)`,
                                }}
                            />
                        </>
                    )}
                </div>,
                document.body,
            )}
            <div ref={wrapperRef} style={{ position: 'relative', zIndex: hoverPanel ? 20 : undefined }}>
                <table style={style ?? {}} className='manual-tasks-table--compact'>
                    <thead>
                        <tr>
                            <th>
                                <div className='manual-tasks-table--compact-header'>
                                    <span>Task</span>
                                    {headerActions && <div className='manual-tasks-table--compact-header-actions'>{headerActions}</div>}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasks?.map((task, idx) => {
                            const rowKey = `${task._id ?? task.text}-${idx}`;
                            const isEvenRow = idx % 2 === 1; // 0-based idx, even rows are every other
                            return (
                                <tr
                                    key={rowKey}
                                    className='manual-tasks-row--compact'
                                    onClick={() => setSelectedTask(task)}
                                    onKeyDown={(event) => handleRowKeyDown(event, task)}
                                    onMouseEnter={(e) => handleRowMouseEnter(e, task, rowKey, isEvenRow)}
                                    onMouseLeave={() => handleRowMouseLeave(rowKey)}
                                    tabIndex={0}
                                    role='button'
                                >
                                    <td className={`${useLargeFont ? 'tasks--medium-text' : ''} manual-task-cell--compact`.trim()}>
                                        <span>{task.text}</span>
                                        <span className='manual-task-dots' aria-hidden='true'>
                                            ⋯
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {hoverPanel && (
                    <div
                        className='manual-task-hover-ext'
                        style={{
                            top: hoverPanel.top - HOVER_HEADER_HEIGHT_PX,
                            height: HOVER_HEADER_HEIGHT_PX + hoverPanel.rowHeight,
                        }}
                        onMouseEnter={handleExtensionMouseEnter}
                        onMouseLeave={handleExtensionMouseLeave}
                    >
                        <div className='manual-task-hover-column'>
                            <span className='manual-task-hover-label'>Created by</span>
                            <span className='manual-task-hover-value' style={{ backgroundColor: hoverPanel.rowBg }}>
                                {getCreatedByWithEdit(hoverPanel.task) || '-'}
                            </span>
                        </div>
                        <div className='manual-task-hover-column'>
                            <span className='manual-task-hover-label'>Activated at</span>
                            <span className='manual-task-hover-value' style={{ backgroundColor: hoverPanel.rowBg }}>
                                {formatDatetime(hoverPanel.task.scheduledAt) || '-'}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {selectedTask && (
                <Dialog title='Task Details' onClose={closeSelectedTask}>
                    <div className='manual-task-details'>
                        <div className='manual-task-details-grid'>
                            <div className='manual-task-details-label'>Task</div>
                            <div>{selectedTask.text}</div>

                            <div className='manual-task-details-label'>Created by</div>
                            <div>{getCreatedByWithEdit(selectedTask) || '-'}</div>

                            <div className='manual-task-details-label'>Created at</div>
                            <div>{formatDatetime(selectedTask.createdAt) || '-'}</div>

                            <div className='manual-task-details-label'>Activated at</div>
                            <div>{formatDatetime(selectedTask.scheduledAt) || '-'}</div>
                        </div>

                        <div className='manual-task-details-actions'>
                            <button onClick={() => handleActionClick(onEditTaskClick)} children='Edit' />
                            <button onClick={() => handleActionClick(onCancelTaskClick)} children='Cancel task' />
                            <button onClick={() => handleActionClick(onCompleteTaskClick)} children='Complete task' />
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    );
}
